// EventDialog.jsx - Modale Gestione Eventi
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, IconButton, Box, Typography,
  Divider, Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DateTimePicker } from '@mui/lab';
import UserQueueFormazione from '../UserQueueFormazione';
import HelperEventFormazione from './helperEventFormazione';
import CalendarButton from "../../../../components/common/CalendarButton";

const EventDialog = ({
  open,
  onClose,
  mode,
  currentEvent,
  setCurrentEvent,
  instructors,
  allUsers,
  selectedUser,
  setSelectedUser,
  currentUserId,
  isManagerIstruttore,
  onSave,
  onDelete,
  onAddUser,
  onDeleteUser,
  onTogglePresenza,
  onToast,
  t
}) => {
   // helper per formattare date/ora

  const handleSave = async () => {
    const { descrizione, start, durata, istruttore1Id, utentiIstr1, utentiIstr2 } = currentEvent;
    const errors = [];

    // Validazioni
    if (!descrizione.trim()) errors.push(t('Descrizione obbligatoria'));
    if (!start) errors.push(t('Ora inizio obbligatoria'));
    if (!durata || durata <= 0) errors.push(t('Durata obbligatoria'));
    if (!istruttore1Id) errors.push(t('Istruttore 1 obbligatorio'));
    if (!utentiIstr1 || utentiIstr1 <= 0) errors.push(t('Numero utenti istr.1 obbligatorio'));
    if (utentiIstr2 == null || utentiIstr2 < 0) errors.push(t('Numero utenti istr.2 obbligatorio'));

    if (errors.length > 0) {
      onToast('Error', errors.join('\n'));
      return;
    }

    const payload = {
      id: currentEvent.id,
      descrizione,
      tipoLezione: currentEvent.tipoLezione,
      start: start.toISOString(),
      durata,
      istruttore1Id,
      istruttore2Id: currentEvent.istruttore2Id,
      utentiIstr1,
      utentiIstr2,
      note: '',
      createdAt: '1979-02-19T06:50:00'
    };

    try {
      await onSave(payload);
    } catch (error) {
      // L'errore è già gestito nel componente padre
    }
  };

  const handleAddUser = async () => {
    if (!selectedUser) return;
    try {
      await onAddUser(selectedUser.id);
    } catch (error) {
      // L'errore è già gestito nel componente padre
    }
  };

  const handleAddCurrentUser = async () => {
    const me = allUsers.find(u => u.id === currentUserId);
    if (me) {
      try {
        await onAddUser(me.id);
      } catch (error) {
        // L'errore è già gestito nel componente padre
      }
    }
  };

  // Calcoli per i controlli
  const eventStart = new Date(currentEvent.start);
  const eventEnd   = new Date(eventStart.getTime() + currentEvent.durata * 60 * 60 * 1000); // +2h
  const now = new Date();
  const isPast = now > eventStart;
  const isBeforeStart = now < eventStart;
  const canCancel = now < new Date(eventStart.getTime() - 150 * 60000); // 2,5 ore prima
  const isIstruttore = [currentEvent.istruttore1Id, currentEvent.istruttore2Id].includes(currentUserId);
  
  // Calcola iscritti attivi (non cancellati)
  const iscrittiAttivi = currentEvent.iscritti.filter(i => i.deletedAt == null);
  const isUserAlreadyEnrolled = iscrittiAttivi.some(i => i.anaUserId === currentUserId);
  const isUserInstructor = currentUserId === currentEvent.istruttore1Id || currentUserId === currentEvent.istruttore2Id;
  const hasAvailableSlots = iscrittiAttivi.length < (currentEvent.utentiIstr1 + currentEvent.utentiIstr2);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        {mode === 'create' ? t('Aggiungi Corso') : t('Modifica Corso')}
        {/* -- ADD TO CALENDAR BUTTON -- */}
        <CalendarButton
          data={{
            title: `Formazione: ${currentEvent.descrizione}`,
            start: eventStart,
            end: eventEnd,
            description: currentEvent.descrizione || '',
            location: ''
          }}
          filename={`formazione_${currentEvent.id || 'evento'}.ics`}
          t={t}
          buttonProps={{
            sx: {
              color: "#2c5282",
              "&:hover": {
                backgroundColor: "#2c5282",
                color: "#fff",
              },
            }
          }}
        />
      </DialogTitle>
      <HelperEventFormazione isManager={isManagerIstruttore} />
      <DialogContent 
          id="ED-user-info" 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1.5, // Ridotto da 2 a 1.5 per compattare gli elementi interni
            pt: '16px !important', // Azzera il padding superiore per avvicinarsi al titolo
            mt: 0, // Rimosso il margin top
            borderTop: '1px solid #e0e0e0'
          }}
        >
        {(isManagerIstruttore || isIstruttore) ? (
          // MODALITÀ EDIT/CREATE - Campi modificabili per Manager
          <>
            <TextField
              id="ED-descrizione"
              label={t('Descrizione')}
              value={currentEvent.descrizione}
              onChange={e => setCurrentEvent(ev => ({ ...ev, descrizione: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              id="ED-tipoLezione"
              select
              label={t('Tipo lezione')}
              value={currentEvent.tipoLezione || 'SSE'}
              onChange={(e) =>
                setCurrentEvent(ev => ({ ...ev, tipoLezione: e.target.value }))
              }
              fullWidth
              SelectProps={{ native: true }}
              sx={{ mt: 1 }}
            >
              <option value="SSE">SSE</option>
              <option value="TS">TS</option>
              <option value="SSE+TS">SSE + TS</option>
              <option value="CNV">CNV</option>
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }} id="ED-start">
              <DateTimePicker
                
                label={t('Inizio')}
                value={currentEvent.start}
                onChange={d => setCurrentEvent(ev => ({ ...ev, start: d }))}
                renderInput={params => <TextField {...params} fullWidth required />}
                ampm={false}
                inputFormat="dd/MM/yyyy HH:mm"
                minutesStep={15}
              />
              <TextField id="ED-durata"
                label={t('Durata (ore)')}
                type="number"
                inputProps={{ min: 1, max: 8 }}
                value={currentEvent.durata}
                onChange={e => setCurrentEvent(ev => ({ ...ev, durata: parseInt(e.target.value) }))}
                fullWidth
                required
              />
            </Box>

            <Autocomplete id="ED-istruttore1"
              options={instructors}
              getOptionLabel={opt => opt.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={instructors.find(i => i.id === currentEvent.istruttore1Id) || null}
              onChange={(_, v) => {
                const newIstruttore1Id = v?.id || null;
                setCurrentEvent(ev => ({
                  ...ev,
                  istruttore1Id: newIstruttore1Id,
                  istruttore2Id: ev.istruttore2Id === newIstruttore1Id ? null : ev.istruttore2Id
                }));
              }}
              renderInput={params => <TextField {...params} label={t('Istruttore 1')} fullWidth required />}
            />

            <Autocomplete id="ED-istruttore2"
              options={instructors}
              getOptionDisabled={(option) => option.id === currentEvent.istruttore1Id}
              getOptionLabel={opt => opt.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={instructors.find(i => i.id === currentEvent.istruttore2Id) || null}
              onChange={(_, v) => {
                const newIstruttore2Id = v?.id || null;
                setCurrentEvent(ev => ({
                  ...ev,
                  istruttore2Id: newIstruttore2Id,
                  istruttore1Id: ev.istruttore1Id === newIstruttore2Id ? null : ev.istruttore1Id
                }));
              }}
              renderInput={params => <TextField {...params} label={t('Istruttore 2')} fullWidth />}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>  
              <TextField id="ED-Nr-istruttore1"
                label={t('Numero utenti Istr. 1')}
                type="number"
                value={currentEvent.utentiIstr1}
                onChange={e => setCurrentEvent(ev => ({ ...ev, utentiIstr1: parseInt(e.target.value) }))}
                fullWidth
                required
              />
              <TextField id="ED-Nr-istruttore2"
                label={t('Numero utenti Istr. 2')}
                type="number"
                value={currentEvent.utentiIstr2}
                onChange={e => setCurrentEvent(ev => ({ ...ev, utentiIstr2: parseInt(e.target.value) }))}
                fullWidth
                required
              />
            </Box>
          </>
        ) : (
          // MODALITÀ READ-ONLY - Visualizzazione per utenti normali
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body1">
              <span style={{ color: '#2c5282', fontWeight: 'bold' }}>{t('Descrizione')}:</span> {currentEvent.descrizione}
            </Typography>
             <Typography variant="body1">
              <span style={{ color: '#2c5282', fontWeight: 'bold' }}>{t('Tipol Lezione')}:</span> {currentEvent.tipoLezione}
            </Typography>
            <Typography variant="body1">
              <span style={{ color: '#2c5282', fontWeight: 'bold' }}>{t('Inizio')}:</span> {currentEvent.start.toLocaleDateString('it-IT')} {currentEvent.start.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
            </Typography>

            <Typography variant="body1">
              <span style={{ color: '#2c5282', fontWeight: 'bold' }}>{t('Durata')}:</span> {currentEvent.durata} {t('ore')}
            </Typography>

            <Typography variant="body1">
              <span style={{ color: '#2c5282', fontWeight: 'bold' }}>{t('Istruttore 1')}:</span> {instructors.find(i => i.id === currentEvent.istruttore1Id)?.name || '-'}
            </Typography>

            {currentEvent.istruttore2Id && (
              <Typography variant="body1">
                <span style={{ color: '#2c5282', fontWeight: 'bold' }}>{t('Istruttore 2')}:</span> {instructors.find(i => i.id === currentEvent.istruttore2Id)?.name || '-'}
              </Typography>
            )}

            <Typography variant="body1">
              <span style={{ color: '#2c5282', fontWeight: 'bold' }}>{t('Posti Istr. 1')}:</span> {currentEvent.utentiIstr1} | <span style={{ color: '#2c5282', fontWeight: 'bold' }}>{t('Posti Istr. 2')}:</span> {currentEvent.utentiIstr2}
            </Typography>
          </Box>
        )}

        <Divider />
        {((!isManagerIstruttore && !isIstruttore) || ((isManagerIstruttore || isIstruttore) && isPast)) && (
          <Box sx={{ 
            p: 2, 
            bgcolor: '#fffde7', 
            border: '1px solid #ffe082', // Bordo leggermente più scuro per definizione
            borderRadius: 1,
            mb: 1
          }}>
            {(!isManagerIstruttore && !isIstruttore) && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 1.5 }}>
                  <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>🕒</Typography>
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'medium', lineHeight: 1.4 }}>
                    <strong>Iscrizione</strong> libera fino all'inizio del corso.
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>⏰</Typography>
                  <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 'medium', lineHeight: 1.4 }}>
                    <strong>Cancellazione</strong> autonoma consentita fino a 2,5 ore prima dell'inizio.
                  </Typography>
                </Box>
              </>
            )}

            {(isManagerIstruttore || isIstruttore) && isPast && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>📌</Typography>
                <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 'medium', lineHeight: 1.4 }}>
                  <strong>CONFERMA le PRESENZE:</strong> Seleziona la casella accanto al nome per confermare la partecipazione. 
                  In caso di assenza, <strong>NON eliminare l'utente</strong> ma lascia la casella vuota per le statistiche.
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {/* Gestione Utenti */}
        <UserQueueFormazione
          userlist={allUsers}
          iscritti={currentEvent.iscritti}
          utentiIstr1={currentEvent.utentiIstr1}
          utentiIstr2={currentEvent.utentiIstr2}
          currentUserId={currentUserId}
          isManagerIstruttore={isManagerIstruttore}
          isIstruttore={isIstruttore}
          isPresenceManagementEnabled={isPast}
          isDeleteEnabledForOperator={canCancel}
          onDelete={onDeleteUser}
          onTogglePresenza={onTogglePresenza}
        />

        {/* Sezione Aggiunta Utenti */}
        {(isManagerIstruttore || isIstruttore) ? (
          // Manager: può aggiungere qualsiasi utente
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">{t("Aggiungi utente alla formazione")}</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
              <Autocomplete
                disableClearable
                fullWidth
                options={allUsers.filter(u =>
                  !currentEvent.iscritti.some(i => i.anaUserId === u.id && i.deletedAt == null) &&
                  u.id !== currentEvent.istruttore1Id &&
                  u.id !== currentEvent.istruttore2Id
                )}
                getOptionLabel={opt => opt.name}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                value={selectedUser}
                onChange={(_, val) => setSelectedUser(val)}
                renderInput={params => (
                  <TextField {...params} label={t("Seleziona utente")} fullWidth />
                )}
                disabled={!hasAvailableSlots}
              />
              <IconButton id="ED-add-user"
                onClick={handleAddUser}
                color="primary"
                disabled={!selectedUser || !hasAvailableSlots}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
        ) : (
          // Utente normale: può iscriversi solo se non è già iscritto
          <>
            {!isUserAlreadyEnrolled &&
              !isUserInstructor &&
              isBeforeStart &&
              hasAvailableSlots && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button variant="contained" onClick={handleAddCurrentUser} id="ED-add-self">
                    {t("Iscrivimi al corso")}
                  </Button>
                </Box>
              )
            }
          </>
        )}
      </DialogContent>

      <DialogActions>
        {/* Pulsante Elimina (solo per Manager in modalità edit) */}
        {mode === 'edit' && isManagerIstruttore && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto', ml: 2 }}>
            <IconButton color="error" onClick={onDelete} id="ED-DeleteEvent">
              <DeleteIcon />
            </IconButton>
          </Box>
        )}

        <Button onClick={onClose} id="ED-CancelEvent">{t('Annulla')}</Button>
        
        {/* Pulsante Salva (solo per Manager o istruttore) */}
        {(isManagerIstruttore || isIstruttore) && (
          <Button id="ED-SaveEvent"
            onClick={handleSave}
            variant="contained"
            disabled={
              !currentEvent.descrizione.trim() ||
              !currentEvent.start ||
              !currentEvent.durata || currentEvent.durata <= 0 ||
              !currentEvent.istruttore1Id ||
              !currentEvent.utentiIstr1 || currentEvent.utentiIstr1 <= 0 ||
              currentEvent.utentiIstr2 == null || currentEvent.utentiIstr2 < 0
            }
          >
            {t('Salva')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EventDialog;